import React, { Component } from 'react';
import gql from 'graphql-tag';
import moment from 'moment';
import PropTypes from 'prop-types';
import { compose, graphql } from 'react-apollo';
import LoadingIndicator from 'components/LoadingIndicator/index';
import { fromIso9075DateString } from 'utils/dates';
import { formatDate } from 'utils/formatting';
import fragments from 'utils/graphql/fragments';
import { CHANGE_PREFERRED_LOCATION_MUTATION } from 'utils/graphql/patronQueries';
import { isLocationPls } from 'utils/locations';
import { hasPlsAccount } from 'utils/roles';
import { errorShape, patronStatusShape, userShape } from 'utils/shapes';
import {
  ACCOUNT_TYPE,
  DISPLAY_NAME,
  ONLINE_EMAIL,
  filterField,
  formatAddressFromPlsStatus,
  formatPhoneNumbersFromPlsStatus
} from '../profileUtils';

class ProfileDetails extends Component {
  constructor(props) {
    super(props);

    this.state = {
      submitting: 0,
      results: []
    };

    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleEdit = this.handleEdit.bind(this);
    this.handleChangeAvatar = this.handleChangeAvatar.bind(this);
    this.handleChangePreferredLocation =
      this.handleChangePreferredLocation.bind(this);
  }

  handleSubmit(newDisplayName, newEmail) {
    const user = this.props.userData && this.props.userData.user;
    if (!newDisplayName || !newDisplayName.trim()) {
      this.setState(state => ({
        results: state.results
          .filter(result => result.name !== DISPLAY_NAME)
          .concat({
            success: false,
            message: 'Invalid display name',
            name: DISPLAY_NAME
          })
      }));
    } else if (newDisplayName !== user.displayName) {
      this.setState(state => ({
        submitting: state.submitting + 1
      }));
      this.props.changeDisplayName(newDisplayName).then(resp => {
        const result = resp && resp.data && resp.data.changeDisplayName;
        this.setState(state => ({
          submitting: state.submitting - 1,
          results: result
            ? state.results
                .filter(result => result.name !== DISPLAY_NAME)
                .concat({ ...result, name: DISPLAY_NAME })
            : state.results
        }));
      });
    }

    if (!newEmail || !newEmail.trim()) {
      this.setState(state => ({
        results: state.results
          .filter(result => result.name !== ONLINE_EMAIL)
          .concat({
            success: false,
            message: 'Invalid email',
            name: ONLINE_EMAIL
          })
      }));
    } else if (newEmail !== user.email) {
      this.setState(state => ({
        submitting: state.submitting + 1
      }));
      this.props.changeEmail(newEmail).then(resp => {
        const result = resp && resp.data && resp.data.changeEmail;
        this.setState(state => ({
          submitting: state.submitting - 1,
          results: result
            ? state.results
                .filter(result => result.name !== ONLINE_EMAIL)
                .concat({ ...result, name: ONLINE_EMAIL })
            : state.results
        }));
      });
    }
  }

  handleEdit() {
    this.setState({
      results: []
    });
  }

  handleChangeAvatar(avatarUrl) {
    this.setState({
      results: []
    });
    this.props.changeAvatar(avatarUrl).then(resp => {
      const result = resp && resp.data && resp.data.changeAvatar;
      this.setState(state => ({
        results: result ? state.results.concat(result) : state.results
      }));
    });
  }

  handleChangePreferredLocation(location) {
    if (location) {
      this.setState({
        results: [],
        submitting: this.state.submitting + 1
      });
      this.props
        .changePreferredLocation(location)
        .then(resp => {
          if (resp && resp.data && resp.data.changePreferredLocation) {
            this.props.rewardsData?.refetch();
            let results = [];
            results.push({
              success: true,
              message:
                'Preferred location successfully changed to ' + location + '.'
            });
            const user = resp.data.changePreferredLocation.user;
            if (isLocationPls(user.preferredLocation) && hasPlsAccount(user)) {
              this.props.onAccountTypeChange(ACCOUNT_TYPE.PLS_ACCOUNT);
            } else {
              this.props.onAccountTypeChange(ACCOUNT_TYPE.CMP_ACCOUNT);
            }
            this.setState(state => ({
              submitting: state.submitting - 1,
              results: results
            }));
          }
        })
        .catch(() => {
          let results = [];
          results.push({
            success: false,
            message: 'Failed to update location, please try again later.'
          });
          this.setState(state => ({
            submitting: state.submitting - 1,
            results: results
          }));
        });
    }
  }

  createUserProfile = userData => {
    const user = userData.user;
    const userProfile = user
      ? {
          displayName: user.displayName,
          onlineEmail: user.email,
          avatarUrl: user.avatarUrl,
          preferredLocation: user.preferredLocation,
          onlineJoinDate: formatDate(
            fromIso9075DateString(user.creationDate),
            'extendedshort'
          ),
          avatarData: user.avatarData ? user.avatarData : ''
        }
      : null;
    return userProfile;
  };

  selectAccountBasedOnType = (accountType, accountStatusData) => {
    if (accountType === ACCOUNT_TYPE.CMP_ACCOUNT) {
      return this.formatCmpPlayerStatus(accountStatusData);
    } else if (accountType === ACCOUNT_TYPE.PLS_ACCOUNT) {
      return this.formatPlsPlayerStatus(accountStatusData);
    }
  };

  formatDateOfBirth = (date, format = 'MM-DD-YYYY') =>
    filterField(date) && formatDate(moment(date, format), 'long');

  formatCmpPlayerStatus = data => {
    if (!data || !data.statusV2 || !data.statusV2.cmpPlayerStatus) {
      return null;
    }

    const cmpPlayerStatus = data.statusV2.cmpPlayerStatus;
    const patronInfo = cmpPlayerStatus.patronInfo;

    const formattedData = {
      patronAccountNumber: cmpPlayerStatus.patronAccountNumber,
      firstName: patronInfo.firstName,
      lastName: patronInfo.lastName,
      birthday: this.formatDateOfBirth(patronInfo.birthDate),
      rewardsJoinDate: patronInfo.rewardsJoinDate,
      gender: filterField(patronInfo.gender),
      email: filterField(patronInfo.email),
      mobilePhone: filterField(patronInfo.mobilePhone),
      homePhone: filterField(patronInfo.homePhone),
      addressLine1: filterField(patronInfo.address1),
      addressLine2: filterField(patronInfo.address2),
      addressCity: filterField(patronInfo.city),
      addressState: filterField(patronInfo.stateProvince),
      addressZip: filterField(patronInfo.postalCode)
    };
    return formattedData;
  };

  formatPlsPlayerStatus = data => {
    if (!data || !data.statusV2 || !data.statusV2.plsPlayerStatus) {
      return null;
    }

    const plsPlayerStatus = data.statusV2.plsPlayerStatus;
    const { homePhone, mobilePhone } = formatPhoneNumbersFromPlsStatus(
      plsPlayerStatus.phones
    );
    const mainAddress = formatAddressFromPlsStatus(plsPlayerStatus.addresses);

    const formattedData = {
      plsPlayerId: plsPlayerStatus.plsPlayerId,
      firstName: plsPlayerStatus.firstName,
      lastName: plsPlayerStatus.lastName,
      birthday: this.formatDateOfBirth(plsPlayerStatus.dateOfBirth),
      email: filterField(plsPlayerStatus.emails[0]),
      gender: plsPlayerStatus.gender,
      rewardsJoinDate: plsPlayerStatus.rewardsJoinDate,
      homePhone: homePhone,
      mobilePhone: mobilePhone,
      addressLine1: mainAddress.addressLine1,
      addressLine2: mainAddress.addressLine2,
      addressCity: mainAddress.city,
      addressState: mainAddress.state,
      addressZip: mainAddress.zip
    };
    return formattedData;
  };

  render() {
    const { userData, activeAccountType, accountStatusData } = this.props;

    if (accountStatusData?.loading) {
      return <LoadingIndicator expand />;
    }
    const profileDetailsError = accountStatusData?.error;
    const userProfile = this.createUserProfile(userData);
    const accountStatusInfo = this.selectAccountBasedOnType(
      activeAccountType,
      accountStatusData
    );

    const FormComponent = this.props.formComponent;
    return (
      <div className="profile-details">
        <FormComponent
          user={userData.user}
          userProfile={userProfile}
          accountProfile={accountStatusInfo}
          profileError={profileDetailsError}
          accountType={this.props.activeAccountType}
          onEdit={this.handleEdit}
          onSubmit={this.handleSubmit}
          changeAvatar={this.handleChangeAvatar}
          onEditPreferredLocation={this.handleChangePreferredLocation}
          submitting={this.state.submitting > 0}
          results={this.state.results}
          editable={this.props.editable}
        />
      </div>
    );
  }
}

const CHANGE_DISPLAY_NAME_MUTATION = gql`
  mutation MyChangeDisplayName($input: ChangeDisplayNameInput!) {
    changeDisplayName(input: $input) {
      success
      message
      user {
        ...UserFields
      }
    }
  }
  ${fragments.user}
`;

const CHANGE_EMAIL_MUTATION = gql`
  mutation MyChangeEmail($input: ChangeEmailInput!) {
    changeEmail(input: $input) {
      success
      message
    }
  }
`;

const CHANGE_AVATAR_MUTATION = gql`
  mutation MyChangeAvatar($input: ChangeAvatarInput!) {
    changeAvatar(input: $input) {
      success
      message
      user {
        userId
        avatarUrl
      }
    }
  }
`;

const withChangeDisplayName = graphql(CHANGE_DISPLAY_NAME_MUTATION, {
  props: ({ mutate }) => ({
    changeDisplayName: newName =>
      mutate({
        variables: { input: { displayName: newName } }
      })
  })
});

const withChangeEmail = graphql(CHANGE_EMAIL_MUTATION, {
  props: ({ mutate }) => ({
    changeEmail: newEmail =>
      mutate({
        variables: { input: { email: newEmail } }
      })
  })
});

const withChangeAvatar = graphql(CHANGE_AVATAR_MUTATION, {
  props: ({ mutate }) => ({
    changeAvatar: avatarUrl =>
      mutate({
        variables: { input: { avatar: avatarUrl } }
      })
  })
});

const withChangePreferredLocation = graphql(
  CHANGE_PREFERRED_LOCATION_MUTATION,
  {
    props: ({ mutate }) => ({
      changePreferredLocation: location =>
        mutate({
          variables: { input: { preferredLocation: location } }
        })
    })
  }
);

ProfileDetails.propTypes = {
  formComponent: PropTypes.any.isRequired,
  userData: PropTypes.shape({
    user: userShape,
    loading: PropTypes.bool,
    error: errorShape
  }),
  statusData: PropTypes.shape({
    status: patronStatusShape,
    loading: PropTypes.bool,
    error: errorShape
  }),
  rewardsData: PropTypes.shape({
    rewardsWithTimeZone: PropTypes.shape({
      rewardTimeZoneHelpText: PropTypes.string,
      patronRewards: PropTypes.arrayOf(
        PropTypes.shape({
          prizeType: PropTypes.string,
          prizeName: PropTypes.string,
          startDate: PropTypes.string,
          endDate: PropTypes.string,
          startDateUTC: PropTypes.string,
          endDateUTC: PropTypes.string,
          redeemedDateUTC: PropTypes.string,
          redeemed: PropTypes.number,
          maxPrizesPerPeriod: PropTypes.number,
          isNewOffer: PropTypes.bool,
          isSoftOffer: PropTypes.bool
        })
      )
    }),
    loading: PropTypes.bool,
    error: errorShape
  }),
  editable: PropTypes.bool
};

export default compose(
  withChangeDisplayName,
  withChangeEmail,
  withChangeAvatar,
  withChangePreferredLocation
)(ProfileDetails);
